import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';
import HeroBanner from '../components/HeroBanner';
import { FiPhone, FiSend } from 'react-icons/fi';
import { MdShoppingCart } from 'react-icons/md';
import { IoIosArrowDown } from 'react-icons/io';
import { FaBars, FaWhatsapp } from 'react-icons/fa';
import { ToastContainer, toast } from "react-toastify";
import { AiOutlineUser } from 'react-icons/ai';
import { MdEdit, MdDelete } from 'react-icons/md';
import "react-toastify/dist/ReactToastify.css";
import PhonePeButton from "../components/PhonePeButton";
import { verifyPhonePePayment } from "../components/PhonePeClient";
import { apiClient } from '../config/apiConfig';

interface CartItem {
  cartItemsId: number;
  cartId: number;
  productId: number;
  productName: string;
  productImage: string;
  variantId?: number;
  variantName?: string;
  productPrice: number;
  variantPrice?: number;
  quantity: number;
}

const isSuccessfulPhonePeStatus = (value?: string | null): boolean => {
  if (!value) return false;
  const normalized = value.toUpperCase();
  return ['SUCCESS', 'COMPLETED', 'SUCCESSFUL', 'PAID'].includes(normalized);
};

const isPendingPhonePeStatus = (value?: string | null): boolean => {
  if (!value) return false;
  const normalized = value.toUpperCase();
  return ['PENDING', 'INITIATED', 'PROCESSING', 'IN_PROGRESS'].includes(normalized);
};

const isVerificationConfirmed = (verifyResponse: any): boolean => {
  if (!verifyResponse) return false;

  const payload = verifyResponse.data || verifyResponse.result || null;

  if (verifyResponse.verified === true || payload?.verified === true) {
    return true;
  }

  if (verifyResponse.success === true) {
    return (
      isSuccessfulPhonePeStatus(verifyResponse.status) ||
      isSuccessfulPhonePeStatus(verifyResponse.state) ||
      isSuccessfulPhonePeStatus(payload?.status) ||
      isSuccessfulPhonePeStatus(payload?.state)
    );
  }

  return false;
};

const isVerificationPending = (verifyResponse: any): boolean => {
  if (!verifyResponse) return false;

  const payload = verifyResponse.data || verifyResponse.result || null;
  const message = String(verifyResponse.message || payload?.message || '').toLowerCase();

  return (
    isPendingPhonePeStatus(verifyResponse.status) ||
    isPendingPhonePeStatus(verifyResponse.state) ||
    isPendingPhonePeStatus(payload?.status) ||
    isPendingPhonePeStatus(payload?.state) ||
    message.includes('not completed') ||
    message.includes('pending')
  );
};

const Checkout: React.FC = () => {
  const loginUserPhone =
    localStorage.getItem("customerPhone") ||
    localStorage.getItem("phone") ||
    localStorage.getItem("mobile") ||
    "";

  const loginUserEmail =
    localStorage.getItem("customerEmail") ||
    localStorage.getItem("email") ||
    sessionStorage.getItem("email") ||
    "";

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: "India",
    streetAddress: "",
    apartment: "",
    city: "",
    postcode: "",
    phone: loginUserPhone,
    email: loginUserEmail,
    paymentMethod: "Cash on Delivery",
  });
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [paid, setPaid] = useState(false);
  const [isCartLoaded, setIsCartLoaded] = useState(false);
  const [pendingPhonePeReturn, setPendingPhonePeReturn] = useState<URLSearchParams | null>(null);

  const navigate = useNavigate(); // If using react-router

  useEffect(() => {
    window.scrollTo(0, 0);

    const name = localStorage.getItem("customerName");
    if (name && name.trim() !== "" && name !== "undefined") {
      setCustomerName(name);
    } else {
      setCustomerName(null);
    }

    const script = document.createElement('script');
    script.src = '/js/main.js';
    script.async = true;
    document.body.appendChild(script);

    const currentParams = new URLSearchParams(window.location.search);
    const looksLikePhonePeReturn =
      currentParams.has('paymentProvider') ||
      currentParams.has('phonepe') ||
      currentParams.has('phonepeStatus') ||
      currentParams.has('paymentStatus') ||
      currentParams.get('status') === 'SUCCESS' ||
      currentParams.get('status') === 'COMPLETED';

    if (looksLikePhonePeReturn) {
      setPendingPhonePeReturn(currentParams);
    }

    const fetchCart = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        if (!customerId) {
          console.error("No customerId found in localStorage");
          return;
        }

        const response = await apiClient.get(
          `/Cart/GetCartByCustomerId?customerId=${customerId}`
        );

        console.log("Cart data:", response.data);

        // ✅ directly set items (API already shaped as items[])
        setCartItems(response.data.items || []);
        setIsCartLoaded(true);

      } catch (error) {
        console.error("Error fetching cart items:", error);
        setIsCartLoaded(true);
      }
    };

    const fetchAddresses = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        if (!customerId) return;

        const response = await apiClient.get(
          `/Orders/GetAddressesByCustomer/${customerId}`
        );

        const fetchedAddresses = Array.isArray(response.data) ? response.data : [];
        setAddresses(fetchedAddresses);

        if (fetchedAddresses.length > 0) {
          const firstAddress = fetchedAddresses[0];
          setSelectedAddressId(firstAddress.addressId);
          setFormData((prev) => ({
            ...prev,
            firstName: firstAddress.firstName || "",
            lastName: firstAddress.lastName || "",
            country: "India",
            streetAddress: firstAddress.streetAddress || "",
            apartment: firstAddress.apartment || "",
            city: firstAddress.city || "",
            postcode: firstAddress.postcode || "",
            phone: firstAddress.phone || loginUserPhone,
            email: firstAddress.email || loginUserEmail,
          }));
        }

      } catch (error) {
        console.error("Error fetching customer addresses", error);
      }
    };

    fetchCart();
    fetchAddresses();

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Helper function to get the effective price (variant price if available, otherwise product price)
  const getItemPrice = (item: CartItem): number => item.variantPrice || item.productPrice;

  const subtotal = cartItems.reduce(
    (sum, item) => sum + getItemPrice(item) * item.quantity,
    0
  );

  const delivery = 0; // can make dynamic later
  const discount = 0; // can apply coupon logic later
  const total = subtotal + delivery - discount;
  const totalCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveAddress = async () => {
    const customerId = localStorage.getItem("customerId");

    if (!customerId) {
      toast.error("Customer not logged in");
      return;
    }

    try {
      const response = await apiClient.post(
        "/Orders/AddAddress",
        {
          customerId: Number(customerId),
          firstName: formData.firstName,
          lastName: formData.lastName,
          country: formData.country,
          streetAddress: formData.streetAddress,
          apartment: formData.apartment,
          city: formData.city,
          postcode: formData.postcode,
          phone: formData.phone,
          email: formData.email
        }
      );

      toast.success("Address added successfully");

      // ✅ refresh address list
      const addrRes = await apiClient.get(
        `/Orders/GetAddressesByCustomer/${customerId}`
      );
      setAddresses(addrRes.data);

      // ✅ auto select new address
      setSelectedAddressId(response.data.addressId);

      // ✅ hide form
      setIsAddingNewAddress(false);

    } catch (error) {
      console.error(error);
      toast.error("Failed to add address");
    }
  };

  const handlePlaceOrder = async () => {
    const customerId = localStorage.getItem("customerId");
    if (!customerId) {
      toast.error("No customerId found in localStorage");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }

    setLoading(true);

    const orderPayload = {
      customerId: parseInt(customerId),
      customerAddress: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        country: formData.country,
        streetAddress: formData.streetAddress,
        apartment: formData.apartment,
        city: formData.city,
        postcode: formData.postcode,
        phone: formData.phone,
        email: formData.email,
      },
      paymentMethod: {
        paymentMethodName: formData.paymentMethod,
      },
      orderItems: cartItems.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        priceAtPurchase: getItemPrice(item),
      })),
    };

    try {
      const response = await apiClient.post(
        "/Orders/PlaceOrder",
        orderPayload
      );

      toast.success("Order placed successfully!");
      console.log("Order response:", response.data);

      // ✅ Clear cart UI after backend removes items
      setCartItems([]);

      // ✅ Navigate to My Orders page
      navigate(`/my-orders`);
    } catch (error) {
      console.error("Error placing order:", error);
      alert("Error placing order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const runPhonePeVerification = async () => {
      if (!pendingPhonePeReturn || !isCartLoaded) {
        return;
      }

      const pendingOrder = sessionStorage.getItem('phonepe-payment-pending');
      if (!pendingOrder) {
        setPendingPhonePeReturn(null);
        return;
      }

      const parsedPendingOrder = JSON.parse(pendingOrder);
      const responseParams = Object.fromEntries(pendingPhonePeReturn.entries());
      const paymentOutcome =
        pendingPhonePeReturn.get('phonepeStatus') ||
        pendingPhonePeReturn.get('paymentStatus') ||
        pendingPhonePeReturn.get('status') ||
        '';

      const normalizedOutcome = paymentOutcome.toUpperCase();

      if (normalizedOutcome && ['PENDING', 'INITIATED', 'PROCESSING', 'IN_PROGRESS'].includes(normalizedOutcome)) {
        toast.info('PhonePe payment is still pending. Complete it in PhonePe and refresh this page after payment finishes.');
        return;
      }

      if (paymentOutcome && !['SUCCESS', 'COMPLETED', 'SUCCESSFUL', 'PAID'].includes(normalizedOutcome)) {
        toast.error('PhonePe payment was not completed. Order not placed.');
        sessionStorage.removeItem('phonepe-payment-pending');
        window.history.replaceState({}, document.title, window.location.pathname);
        setPendingPhonePeReturn(null);
        return;
      }

      try {
        const verifyResponse = await verifyPhonePePayment({
          merchantOrderId: parsedPendingOrder?.merchantOrderId || pendingPhonePeReturn.get('merchantOrderId') || undefined,
          orderId: pendingPhonePeReturn.get('orderId') || parsedPendingOrder?.orderId || undefined,
          transactionId: pendingPhonePeReturn.get('transactionId') || pendingPhonePeReturn.get('txnId') || undefined,
          paymentId: pendingPhonePeReturn.get('paymentId') || undefined,
          status: paymentOutcome || undefined,
          phonePeParams: responseParams,
        });

        if (isVerificationConfirmed(verifyResponse)) {
          toast.success('PhonePe payment successful!');
          sessionStorage.removeItem('phonepe-payment-pending');
          window.history.replaceState({}, document.title, window.location.pathname);
          setPendingPhonePeReturn(null);
          await handlePlaceOrder();
          return;
        }

        if (isVerificationPending(verifyResponse)) {
          toast.info('PhonePe payment is still pending. Complete it in PhonePe and refresh this page after payment finishes.');
          return;
        }

        throw new Error(verifyResponse?.message || 'PhonePe verification failed');
      } catch (error) {
        console.error('PhonePe verification error:', error);
        toast.error('PhonePe payment verification failed. Order not placed.');
        sessionStorage.removeItem('phonepe-payment-pending');
        window.history.replaceState({}, document.title, window.location.pathname);
        setPendingPhonePeReturn(null);
      }
    };

    runPhonePeVerification();
  }, [isCartLoaded, pendingPhonePeReturn]);

  const handleSelectAddress = async (addressId: number) => {
    setSelectedAddressId(addressId);
    setIsAddingNewAddress(false);
    setEditingAddressId(null);

    try {
      const response = await apiClient.get(
        `/Orders/GetAddressById/${addressId}`
      );

      const a = response.data;

      setFormData({
        firstName: a.firstName,
        lastName: a.lastName,
        country: "India",
        streetAddress: a.streetAddress,
        apartment: a.apartment,
        city: a.city,
        postcode: a.postcode,
        phone: a.phone || loginUserPhone,
        email: a.email || loginUserEmail,
        paymentMethod: formData.paymentMethod,
      });

    } catch (error) {
      console.error("Error loading address", error);
    }
  };

  const handleEditAddress = async (addressId: number) => {
    setEditingAddressId(addressId);
    setIsAddingNewAddress(true);

    try {
      const response = await apiClient.get(
        `/Orders/GetAddressById/${addressId}`
      );

      const a = response.data;

      setFormData({
        firstName: a.firstName,
        lastName: a.lastName,
        country: "India",
        streetAddress: a.streetAddress,
        apartment: a.apartment,
        city: a.city,
        postcode: a.postcode,
        phone: a.phone || loginUserPhone,
        email: a.email || loginUserEmail,
        paymentMethod: formData.paymentMethod,
      });

    } catch (error) {
      console.error("Error loading address", error);
      toast.error("Failed to load address");
    }
  };

  const handleUpdateAddress = async () => {
    if (!editingAddressId) return;

    const customerId = localStorage.getItem("customerId");

    if (!customerId) {
      toast.error("Customer not logged in");
      return;
    }

    try {
      await apiClient.put(
        `/Orders/UpdateAddress/${editingAddressId}`,
        {
          customerId: Number(customerId),
          firstName: formData.firstName,
          lastName: formData.lastName,
          country: formData.country,
          streetAddress: formData.streetAddress,
          apartment: formData.apartment,
          city: formData.city,
          postcode: formData.postcode,
          phone: formData.phone,
          email: formData.email
        }
      );

      toast.success("Address updated successfully");

      // ✅ refresh address list
      const addrRes = await apiClient.get(
        `/Orders/GetAddressesByCustomer/${customerId}`
      );
      setAddresses(addrRes.data);

      // ✅ hide form and reset edit mode
      setIsAddingNewAddress(false);
      setEditingAddressId(null);

    } catch (error) {
      console.error(error);
      toast.error("Failed to update address");
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }

    try {
      await apiClient.delete(
        `/Orders/DeleteAddress/${addressId}`
      );

      toast.success("Address deleted successfully");

      // ✅ refresh address list
      const customerId = localStorage.getItem("customerId");
      if (customerId) {
        const addrRes = await apiClient.get(
          `/Orders/GetAddressesByCustomer/${customerId}`
        );
        setAddresses(addrRes.data);
      }

      // ✅ reset selection if deleted address was selected
      if (selectedAddressId === addressId) {
        setSelectedAddressId(null);
      }

    } catch (error) {
      console.error(error);
      toast.error("Failed to delete address");
    }
  };
  const fetchCart = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      if (!customerId) {
        console.error("No customerId found in localStorage");
        return;
      }

      const response = await apiClient.get(
        `/Cart/GetCartByCustomerId?customerId=${customerId}`
      );

      console.log("Cart data:", response.data);

      // ✅ directly set items (API already shaped as items[])
      setCartItems(response.data.items || []);

    } catch (error) {
      console.error("Error fetching cart items:", error);
    } finally {
      setLoading(false); // ✅ stop loading spinner
    }
  };

  const handleIncrease = async (item: CartItem) => {
    try {
      const updatedCart = cartItems.map((i) =>
        i.cartItemsId === item.cartItemsId
          ? { ...i, quantity: i.quantity + 1 }
          : i
      );
      setCartItems(updatedCart);

      // Optional: update quantity in backend
      await apiClient.put(`/Cart/UpdateQuantity`, {
        cartItemsId: item.cartItemsId,
        quantity: item.quantity + 1,
      });
    } catch (error) {
      console.error("Error increasing quantity:", error);
    }
  };

  const handleDecrease = async (item: CartItem) => {
    if (item.quantity <= 1) return; // prevent 0 quantity
    try {
      const updatedCart = cartItems.map((i) =>
        i.cartItemsId === item.cartItemsId
          ? { ...i, quantity: i.quantity - 1 }
          : i
      );
      setCartItems(updatedCart);

      // Optional: update quantity in backend
      await apiClient.put(`/Cart/UpdateQuantity`, {
        cartItemsId: item.cartItemsId,
        quantity: item.quantity - 1,
      });
    } catch (error) {
      console.error("Error decreasing quantity:", error);
    }
  };
  const totalCounts = cartItems.length;
  return (
    <div className="goto-here">
      {/* Top Bar */}
      <div className="py-1 bg-primary w-100">
        <div className="container">
          <div className="row d-flex align-items-center text-white">
            <div className="col-12 col-md d-flex align-items-center mb-1 mb-md-0">
              <FiPhone size={14} className="mr-2" />
              <span>+91 9148761750</span>
            </div>

            <div className="col-12 col-md d-flex align-items-center mb-1 mb-md-0">
              <FiSend size={14} className="mr-2" />
              <span>care@fhome.shop</span>
            </div>

            <div className="col-12 col-md text-md-right">
              <span>3-5 Business days delivery & Free Returns</span>
            </div>
          </div>
        </div>
      </div>
      {/* Navbar */}
      <nav
        className="navbar navbar-expand-lg navbar-dark ftco_navbar bg-dark ftco-navbar-light"
        id="ftco-navbar"
      >
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="/">
            <img
              src="/images/fhome-logo.jpg"
              alt=""
              style={{
                height: '55px',
                marginRight: '8px',
                borderRadius: '50%',
                width: '80px',
              }}
            />
            <span
              style={{
                fontWeight: 'bold',
                fontSize: '20px',
                color: '#28a745',
              }}
            ></span>
          </a>
          <button
            className="navbar-toggler"
            type="button"
            data-toggle="collapse"
            data-target="#ftco-nav"
            aria-controls="ftco-nav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <FaBars className="mr-2" /> Menu
          </button>
          <div className="collapse navbar-collapse" id="ftco-nav">
            <ul className="navbar-nav ml-auto">
              <li className="nav-item">
                <a href="/" className="nav-link">
                  Home
                </a>
              </li>
              <li className="nav-item active dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="#"
                  id="dropdown04"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  Shop
                </a>
                <div className="dropdown-menu" aria-labelledby="dropdown04">
                  <a className="dropdown-item" href="/shop">
                    Shop
                  </a>
                </div>
              </li>
              <li className="nav-item">
                <a href="/about" className="nav-link">
                  About
                </a>
              </li>
              {/* <li className="nav-item">
                <a href="/blog" className="nav-link">
                  Blog
                </a>
              </li> */}
              <li className="nav-item">
                <a href="/contact" className="nav-link">
                  Contact
                </a>
              </li>
              <li className="nav-item cta cta-colored"> <a href="/cart" className="nav-link">
                <span><MdShoppingCart size={18} /></span> [{totalCounts}]
              </a></li>
              <li className="nav-item">
                <a
                  className="nav-link d-flex align-items-center"
                  href="/my-account"
                  style={{ cursor: "pointer", gap: "6px" }}
                >
                  <AiOutlineUser size={27} style={{ color: "#898989" }} />
                  
                  {customerName ? (
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999",
                        maxWidth: "120px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={`Hi, ${customerName}`}
                    >
                      Hi, {customerName}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#999"
                      }}
                    >
                      Login
                    </span>
                  )}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <HeroBanner image="images/fhome-ghee-grass.jpg">
        <div className="row no-gutters slider-text align-items-center justify-content-center">
          <div className="col-md-9 ftco-animate text-center">
            <p className="breadcrumbs">
              <span className="mr-2">
                <a href="/">Home</a>
              </span>{' '}
              <span>Checkout</span>
            </p>
            <h1 className="mb-0 bread">Checkout</h1>
          </div>
        </div>
      </HeroBanner>
      {/* Saved Addresses Section */}


      {/* Checkout Section */}
      <section className="ftco-section">
        <div className="container">
          {addresses.length > 0 ? (
            <div className="mb-4">
              {addresses.map((address) => {
                const isSelected =
                  selectedAddressId === address.addressId;

                return (
                  <div
                    key={address.addressId}
                    className={`card address-card mb-3 ${isSelected ? "selected" : ""
                      }`}
                    onClick={() => handleSelectAddress(address.addressId)}
                  >
                    <div className="card-body">
                      <div className="d-flex align-items-start gap-2">

                        {/* Radio */}
                        <div className="pt-1">
                          <input
                            type="radio"
                            name="selectedAddress"
                            className="form-check-input"
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleSelectAddress(address.addressId)}
                          />
                        </div>
                 
                        {/* Address Content */}
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <h6 className="fw-bold mb-1">
                              {address.firstName} {address.lastName}
                            </h6>

                            <div className="d-flex gap-2 align-items-center">
                              {isSelected && (
                                <span className="badge bg-success">
                                  Selected
                                </span>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditAddress(address.addressId);
                                }}
                                title="Edit address"
                              >
                                <MdEdit size={18} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAddress(address.addressId);
                                }}
                                title="Delete address"
                              >
                                <MdDelete size={18} />
                              </button>
                            </div>
                          </div>

                          <p className="mb-1 text-muted small">
                            📍 {address.streetAddress}
                            {address.apartment && `, ${address.apartment}`}
                          </p>

                          <p className="mb-1 text-muted small">
                            {address.city} • {address.postcode}
                          </p>

                          <p className="mb-0 small fw-semibold">
                            📞 {address.phone}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add New Address Button */}
              <button
                type="button"
                className="btn btn-outline-primary w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                onClick={() => {
                  setSelectedAddressId(null);
                  setIsAddingNewAddress(true);
                  setEditingAddressId(null);

                  setFormData({
                    firstName: "",
                    lastName: "",
                    country: "India",
                    streetAddress: "",
                    apartment: "",
                    city: "",
                    postcode: "",
                    phone: loginUserPhone,
                    email: loginUserEmail,
                    paymentMethod: "Cash on Delivery",
                  });
                }}
              >
                ➕ Add New Address
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted mb-3">
                No saved addresses found
              </p>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={() => {
                  setIsAddingNewAddress(true);
                  setFormData((prev) => ({
                    ...prev,
                    country: "India",
                    phone: loginUserPhone,
                    email: loginUserEmail,
                  }));
                }}
              >
                ➕ Add New Address
              </button>
            </div>
          )}
          <div className="row justify-content-center">
            <div className="col-xl-7 ftco-animate">
              {isAddingNewAddress && (
                <form action="#" className="billing-form">
                  <h3 className="mb-4 billing-heading">
                    {editingAddressId ? "Edit Address" : "Billing Details"}
                  </h3>
                  <div className="row align-items-end">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="firstname">First Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="First Name"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="lastname">Last Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Last Name"
                        />
                      </div>
                    </div>
                    <div className="w-100"></div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="country">State / Country</label>
                        <div className="select-wrap">
                          <div className="icon">
                            <IoIosArrowDown size={20} />
                          </div>
                          <select className="form-control"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            disabled
                            aria-readonly="true">
                            <option value="India">India</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="w-100"></div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="streetaddress">Street Address</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="House number and street name"
                          name="streetAddress"
                          value={formData.streetAddress}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Appartment, suite, unit etc: (optional)"
                          name="apartment"
                          value={formData.apartment}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="w-100"></div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="towncity">Town / City</label>
                        <input type="text" className="form-control" placeholder="" name="city" value={formData.city} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="postcodezip">Postcode / ZIP *</label>
                        <input type="text" className="form-control" placeholder="" name="postcode" value={formData.postcode} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="w-100"></div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="phone">Phone</label>
                        <input type="text" className="form-control" placeholder="" name="phone" value={formData.phone} onChange={handleChange} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="emailaddress">Email Address</label>
                        <input type="text" className="form-control" placeholder="" name="email" value={formData.email} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={editingAddressId ? handleUpdateAddress : handleSaveAddress}
                    >
                      {editingAddressId ? "Update Address" : "Add Address"}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsAddingNewAddress(false);
                        setEditingAddressId(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="col-xl-5">
              <div className="row mt-5 pt-3">
                <div className="col-md-12 d-flex mb-5">
                  <div className="cart-detail cart-total p-3 p-md-4">
                    <h3 className="billing-heading mb-4">Cart Total</h3>
                    <p className="d-flex">
                      <span>Subtotal</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </p>
                    <p className="d-flex">
                      <span>Delivery</span>
                      <span>₹{delivery.toFixed(2)}</span>
                    </p>
                    <p className="d-flex">
                      <span>Discount</span>
                      <span>₹{discount.toFixed(2)}</span>
                    </p>
                    <hr />
                    <p className="d-flex total-price">
                      <span>Total</span>
                      <span>₹{total.toFixed(2)}</span>
                    </p>
                  </div>
                </div>

                <div className="col-md-12">
                  <div className="cart-detail p-3 p-md-4">
                    <h3 className="billing-heading mb-4">Payment Method</h3>
                    <div className="form-group">
                      <div className="col-md-12">
                        <div className="radio">
                          <label>
                            <input
                              type="radio"
                              name="paymentMethod"
                              className="mr-2"
                              value="Cash on Delivery"
                              checked={formData.paymentMethod === "Cash on Delivery"}
                              onChange={handleChange}
                            />{' '}
                            Cash on Delivery
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <div className="col-md-12">
                        <div className="radio">
                          <label>
                            <input
                              type="radio"
                              name="paymentMethod"
                              className="mr-2"
                              value="PhonePe"
                              checked={formData.paymentMethod === "PhonePe"}
                              onChange={handleChange}
                            />{' '}
                            PhonePe
                          </label>
                        </div>
                        {formData.paymentMethod === "PhonePe" && (
                          <div style={{ padding: 20 }}>
                            <PhonePeButton
                              amountRupees={total}
                              prefill={{
                                name: formData.firstName + " " + formData.lastName,
                                email: formData.email,
                                contact: formData.phone
                              }}
                              onError={(err) => {
                                console.error(err);
                                toast.error("Payment failed. Order not placed.");
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {/* <div>
                      <h1>Razorpay Payment</h1>

                      <RazorpayButton
                        amountRupees={total}
                        onSuccess={() => setPaid(true)} // ✅ function reference
                      />

                    </div> */}
                    {/* <div className="form-group">
                      <div className="col-md-12">
                        <div className="radio">
                          <label>
                            <input
                              type="radio"
                              name="paymentMethod"
                              className="mr-2"
                              value="Paypal"
                              checked={formData.paymentMethod === "Paypal"}
                              onChange={handleChange}
                            />{' '}
                            Paypal
                          </label>
                        </div>
                      </div>
                    </div> */}
                    <p>
                      <button
                        type="button"
                        className="btn btn-primary py-3 px-4"
                        onClick={(e) => {
                          e.preventDefault();
                          
                          if (!formData.firstName || !formData.lastName || !formData.phone || !formData.email) {
                            toast.error("Please fill in all address details");
                            return;
                          }
                          
                          if (formData.paymentMethod === "Cash on Delivery") {
                            // Direct order placement for COD
                            handlePlaceOrder();
                          } else if (formData.paymentMethod === "PhonePe") {
                            // Trigger PhonePe payment initiation
                            document.getElementById("phonepe-btn")?.click();
                          } else {
                            toast.error("Please select a payment method");
                          }
                        }}
                      >
                        Place Order
                      </button>

                      <div>
                        <ToastContainer
                          position="top-right"
                          autoClose={3000}
                          hideProgressBar={false}
                          newestOnTop={false}
                          closeOnClick
                          pauseOnHover
                          draggable
                          theme="colored"
                        />
                      </div>
                    </p>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="ftco-section ftco-no-pt ftco-no-pb py-5 bg-light">
        <div className="container py-4">
          <div className="row d-flex justify-content-center py-5">
            <div className="col-md-6">
              <h2 style={{ fontSize: 22 }} className="mb-0">
                Subcribe to our Newsletter
              </h2>
              <span>
                Get e-mail updates about our latest shops and special offers
              </span>
            </div>
            <div className="col-md-6 d-flex align-items-center">
              <form action="#" className="subscribe-form">
                <div className="form-group d-flex">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter email address"
                  />
                  <input
                    type="submit"
                    value="Subscribe"
                    className="submit px-3"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <Footer />

      {/* WhatsApp Float */}
      <a
        href="https://wa.me/919148761750"
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp size={40} color="#25D366" />
      </a>
    </div>
  );
};

export default Checkout;

